import { useEffect, useState } from 'react';
import { payrollGlobalApi as papi } from '../../../api/payrollGlobal.js';

/** Tax jurisdictions + their statutory rules (brackets/rates as JSON config). */
export default function JurisdictionsPage() {
  const [jurisdictions, setJurisdictions] = useState([]);
  const [rules, setRules] = useState([]);
  const [localizations, setLocalizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [j, r, l] = await Promise.all([papi.listJurisdictions(), papi.listStatutoryRules(), papi.listLocalizations()]);
      setJurisdictions(j?.items || []);
      setRules(r?.items || []);
      setLocalizations(l?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function createJurisdiction(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const form = e.currentTarget;
    setBusy(true); setError(null);
    try {
      await papi.createJurisdiction({ code: fd.get('code'), name: fd.get('name'), country: fd.get('country') });
      form.reset();
      await load();
    } catch (e2) { setError(e2.response?.data?.message || 'Save failed'); }
    finally { setBusy(false); }
  }

  async function createRule(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const form = e.currentTarget;
    setBusy(true); setError(null);
    try {
      let config = {};
      try { config = JSON.parse(fd.get('config') || '{}'); } catch { throw new Error('Config must be valid JSON'); }
      await papi.createStatutoryRule({ jurisdictionId: fd.get('jurisdictionId'), ruleType: fd.get('ruleType'), config });
      form.reset();
      await load();
    } catch (e2) { setError(e2.response?.data?.message || e2.message || 'Save failed'); }
    finally { setBusy(false); }
  }

  return (
    <section data-testid="payroll-global-jurisdictions-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header"><div><h1>Jurisdictions &amp; Statutory Rules</h1><p className="muted">Pluggable localizations interpret these brackets/rates per country.</p></div></header>
      {error && <div className="alert error" data-testid="payroll-global-jurisdictions-error">{error}</div>}

      <div className="card">
        <h3>Available localizations</h3>
        <p className="muted" data-testid="payroll-global-localization-list">{localizations.map((l) => `${l.code} (${l.name})`).join(' · ') || '—'}</p>
      </div>

      <form onSubmit={createJurisdiction} className="card form-card grid-form">
        <label>Code<input name="code" data-testid="payroll-global-jurisdiction-code-input" placeholder="ID / US-CA" required /></label>
        <label>Name<input name="name" data-testid="payroll-global-jurisdiction-name-input" required /></label>
        <label>Country<input name="country" data-testid="payroll-global-jurisdiction-country-input" placeholder="ID / US" required /></label>
        <div className="full actions"><button className="btn primary" type="submit" data-testid="payroll-global-jurisdiction-create" disabled={busy}>Add jurisdiction</button></div>
      </form>

      <div className="card">
        <h3>Jurisdictions</h3>
        <table className="data-table" data-testid="payroll-global-jurisdiction-list">
          <thead><tr><th>Code</th><th>Name</th><th>Country</th></tr></thead>
          <tbody>
            {jurisdictions.map((j) => (
              <tr key={j.id} data-testid={`payroll-global-jurisdiction-row-${j.id}`}><td>{j.code}</td><td>{j.name}</td><td>{j.country}</td></tr>
            ))}
            {!loading && jurisdictions.length === 0 && <tr><td colSpan={3} className="muted">No jurisdictions yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <form onSubmit={createRule} className="card form-card grid-form">
        <label>Jurisdiction
          <select name="jurisdictionId" data-testid="payroll-global-rule-jurisdiction-input" required>
            <option value="">— select —</option>
            {jurisdictions.map((j) => <option key={j.id} value={j.id}>{j.code}</option>)}
          </select>
        </label>
        <label>Rule type
          <select name="ruleType" data-testid="payroll-global-rule-type-input" defaultValue="income_tax">
            <option value="income_tax">income_tax</option><option value="social">social</option><option value="pension">pension</option><option value="other">other</option>
          </select>
        </label>
        <label className="full">Config (JSON)<input name="config" data-testid="payroll-global-rule-config-input" defaultValue='{"rate":0.1}' /></label>
        <div className="full actions"><button className="btn primary" type="submit" data-testid="payroll-global-rule-create" disabled={busy}>Add statutory rule</button></div>
      </form>

      <div className="card">
        <h3>Statutory rules</h3>
        <table className="data-table" data-testid="payroll-global-rule-list">
          <thead><tr><th>Rule type</th><th>Config</th><th>Effective from</th></tr></thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} data-testid={`payroll-global-rule-row-${r.id}`}>
                <td>{r.rule_type}</td><td><code>{JSON.stringify(r.config)}</code></td><td>{String(r.effective_from).slice(0, 10)}</td>
              </tr>
            ))}
            {!loading && rules.length === 0 && <tr><td colSpan={3} className="muted">No rules yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
